import { openOAuthConnectPopup } from '../oauth-connect-lifecycle'
import { FC, useEffect, useState } from 'react';
import { queryClient, trpc, trpcClient } from '@/lib/trpc';
import { Integration, IntegrationFeature, IntegrationProvider, ConfigModalProps } from '../types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog';
import { Button } from '@deepguide-ai/ui/button';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Linear icon wrapper component
 */
const LinearIconWrapper: FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="linearGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#5C6DFF" />
        <stop offset="100%" stopColor="#8C4BFF" />
      </linearGradient>
    </defs>
    <path
      d="M4.8 2C3.2536 2 2 3.2536 2 4.8V9.6C2 10.9255 3.07452 12 4.4 12H9.2C10.7464 12 12 10.7464 12 9.2V4.4C12 3.07452 10.9255 2 9.6 2H4.8Z"
      fill="url(#linearGradient)"
    />
    <path
      d="M14.8 12C13.2536 12 12 13.2536 12 14.8V19.6C12 20.9255 13.0745 22 14.4 22H19.2C20.7464 22 22 20.7464 22 19.2V14.4C22 13.0745 20.9255 12 19.6 12H14.8Z"
      fill="url(#linearGradient)"
      opacity="0.6"
    />
  </svg>
);

const LinearConfigModal: FC<ConfigModalProps> = ({
  open,
  onOpenChange,
  workspaceId,
  onComplete,
  integration,
}) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [awaitingAuthResult, setAwaitingAuthResult] = useState(false);

  useEffect(() => {
    if (!open) {
      setIsConnecting(false);
      setAwaitingAuthResult(false);
    }
  }, [open]);

  useEffect(() => {
    if (open && awaitingAuthResult && integration?.id) {
      setIsConnecting(false);
      setAwaitingAuthResult(false);
      onComplete();
    }
  }, [awaitingAuthResult, integration?.id, onComplete, open]);

  const handleConnect = async () => {
    setIsConnecting(true);
    setAwaitingAuthResult(true);
    try {
      await LinearProvider.handleConnect({
        workspaceId,
        onSuccess: () => {
          setIsConnecting(false);
          setAwaitingAuthResult(false);
          onComplete();
        },
        onFinish: (status) => {
          if (status === 'error') {
            setIsConnecting(false);
            setAwaitingAuthResult(false);
          } else if (status === 'closed') {
            setIsConnecting(false);
          }
        },
      });
    } catch (error) {
      console.error('Failed to connect to Linear:', error);
      setIsConnecting(false);
      setAwaitingAuthResult(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect to Linear</DialogTitle>
          <DialogDescription>
            Connect your Linear account to import issues and keep your documentation in sync with your product backlog.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 p-4">
          <div className="text-sm text-muted-foreground">
            You'll be redirected to Linear to authorize the connection. Make sure pop-ups are enabled for this site.
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isConnecting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConnect}
              disabled={isConnecting}
            >
              {isConnecting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                'Connect to Linear'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const LinearProvider: IntegrationProvider = {
  id: 'Linear',
  name: 'Linear',
  description: 'Connect to Linear to import issues from your product backlog',
  Icon: LinearIconWrapper,
  ConfigModal: LinearConfigModal,

  getFeatures: (): IntegrationFeature[] => [
    {
      id: 'issues',
      name: 'Issues',
      description: 'Import Linear issues',
      enabled: true,
    },
    {
      id: 'projects',
      name: 'Projects',
      description: 'Import Linear projects',
      enabled: false,
    },
  ],

  handleConnect: async ({ workspaceId, features, redirectUrl, onSuccess, onFinish }) => {
    const targetRedirectUrl =
      redirectUrl || `${window.location.origin}/auth-callback/Linear`;

    const data = await trpcClient.integrations.initLinearAuth.mutate({
      workspaceId,
      features,
      redirectUrl: targetRedirectUrl,
      providerType: 'Linear',
    });

    if (!data.authUrl) {
      console.error('No Linear auth URL returned from initialization');
      return;
    }

    openOAuthConnectPopup({
      authUrl: data.authUrl, windowName: 'linear-auth', provider: 'Linear',
      onSuccess: result => {
        void queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() });
        onSuccess?.(result);
      },
      onError: message => toast.error(message),
      onPopupBlocked: () => toast.error('OAuth popup was blocked. Allow popups and try again.'),
      onFinish: onFinish,
    })
  },

  onConnectComplete: async ({ code, state }) => {
    const result = await trpcClient.integrations.completeLinearAuth.mutate({
      code,
      state,
    });

    queryClient.invalidateQueries({
      queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
    });

    return result;
  },

  syncData: async () => {
    await trpcClient.tickets.ingest.mutate({
      platforms: ['LINEAR'],
    });

    queryClient.invalidateQueries({
      predicate: ({ queryKey }) =>
        Array.isArray(queryKey) && queryKey[0] === 'tickets.list',
    });

    queryClient.invalidateQueries({
      queryKey: trpc.tickets.getIngestionStatus.queryKey({}),
    });
  },

  shouldDisplay: () => true,
};
